import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardCheck,
  MessageSquareOff,
  Zap,
  Archive,
  CircuitBoard,
  LayoutGrid,
} from "lucide-react";

const OUTCOMES = [
  {
    icon: ClipboardCheck,
    title: "Better RFQs",
    metric: "+3.2×",
    metricLabel: "more complete",
    body:
      "Structured fields ensure every requirement is captured before a supplier ever sees the request.",
  },
  {
    icon: MessageSquareOff,
    title: "Fewer Supplier Questions",
    metric: "−78%",
    metricLabel: "back-and-forth",
    body:
      "Manufacturers receive the full picture upfront — specs, terms, documents, expectations.",
  },
  {
    icon: Zap,
    title: "Faster Quotations",
    metric: "5–10×",
    metricLabel: "faster turnaround",
    body:
      "Verified suppliers respond in hours, not weeks. Compare apples to apples in one view.",
  },
  {
    icon: Archive,
    title: "Better Documentation",
    metric: "100%",
    metricLabel: "audit-ready",
    body:
      "Every RFQ, attachment, revision and message stays inside the Workspace. Forever searchable.",
  },
  {
    icon: CircuitBoard,
    title: "Ready for Procurement",
    metric: "1-click",
    metricLabel: "to CommodityBid™",
    body:
      "Turn any RFQ into a live procurement auction without re-entering a single field.",
  },
  {
    icon: LayoutGrid,
    title: "One Workspace",
    metric: "0",
    metricLabel: "tools to switch",
    body:
      "RFQ™ shares the same data plane as FreightIQ™, Inspection™ and Live Shipment Tracking™.",
  },
];

export default function Outcomes() {
  return (
    <section
      id="outcomes"
      data-testid="outcomes-section"
      className="relative py-24 sm:py-32 border-t border-black/[0.06]"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">Business Outcomes</span>
            <h2 className="mt-4 font-display font-black tracking-[-0.035em] text-[40px] sm:text-[56px] leading-[0.98]">
              Procurement teams who replace
              <br />
              <span className="text-black/40">emails with RFQ™.</span>
            </h2>
          </div>
          <p className="md:max-w-md text-[15px] leading-relaxed text-black/60">
            Measured across pilot Workspaces sourcing from verified Turkish
            manufacturers across textiles, food, machinery and consumer goods.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {OUTCOMES.map(({ icon: Icon, title, metric, metricLabel, body }, i) => (
            <motion.article
              key={title}
              data-testid={`outcome-card-${i}`}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group rounded-2xl border border-black/[0.06] bg-white p-6 sm:p-7 hover:border-black/[0.14] hover:shadow-card transition-all duration-300 hover:-translate-y-0.5"
            >
              <div className="flex items-start justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-black/[0.04] text-black/75 group-hover:bg-[#0044FF]/10 group-hover:text-[#0044FF] transition-colors">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="text-right">
                  <div className="font-display text-[28px] font-black tracking-tight leading-none text-[#0044FF]">
                    {metric}
                  </div>
                  <div className="mt-1 font-mono text-[10px] tracking-[0.18em] text-black/45">
                    {metricLabel.toUpperCase()}
                  </div>
                </div>
              </div>
              <h3 className="mt-5 font-display text-[19px] sm:text-[20px] font-semibold tracking-tight">
                {title}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed text-black/60">{body}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
